import { useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import { matrixClient } from "../services/matrixClient";
import { getVisibleRooms } from "../services/roomListing";
import { selfUserAtom } from "../atoms";
import { ADMIN_ROLE } from "../constants";

const POLL_INTERVAL = 30_000; // 30 seconds

/**
 * Hook that polls for new unread messages and shows browser notifications.
 * Requests notification permission on mount.
 */
export default function useNotifications() {
	const prevCounts = useRef<Record<string, number>>({});
	const roomNames = useRef<Record<string, string>>({});
	const selfUser = useRecoilValue(selfUserAtom);
	const role = selfUser?.role;
	const idClub = selfUser?.idClub;

	useEffect(() => {
		if (!matrixClient.isLoggedIn()) return;
		// Wait for selfUser: room listing is role-dependent
		// (matrix/rooms is super-admin-only, club roles use club_chats)
		if (!role) return;

		// Request permission on mount
		if ("Notification" in window && Notification.permission === "default") {
			Notification.requestPermission();
		}

		const poll = async () => {
			try {
				const counts = await matrixClient.getUnreadCounts();
				const prev = prevCounts.current;

				// Find rooms with new unreads
				for (const [roomId, count] of Object.entries(counts)) {
					const prevCount = prev[roomId] || 0;
					if (count > prevCount && prevCount >= 0) {
						showNotification(roomId, count - prevCount);
					}
				}

				prevCounts.current = counts;
			} catch {
				// non-critical
			}
		};

		// Cache room names for notifications
		const cacheRoomNames = async () => {
			try {
				const rooms = await getVisibleRooms(matrixClient, {
					isSuperAdmin: role === ADMIN_ROLE.ADMIN,
					clubId: idClub,
				});
				for (const room of rooms) {
					roomNames.current[room.id] = room.name || room.id;
				}
			} catch {
				// non-critical
			}
		};

		const showNotification = (roomId: string, newCount: number) => {
			if (!("Notification" in window) || Notification.permission !== "granted") return;

			const name = roomNames.current[roomId] || "Chat";
			const n = new Notification("Bridges", {
				body: `${newCount} new message${newCount > 1 ? "s" : ""} in ${name}`,
				icon: "/favicon.ico",
				tag: `bridges-${roomId}`, // replace previous notification for same room
			});

			// Auto-close after 5s
			setTimeout(() => n.close(), 5000);
		};

		// Initial fetch (don't notify on first load)
		const init = async () => {
			await cacheRoomNames();
			try {
				const counts = await matrixClient.getUnreadCounts();
				prevCounts.current = counts;
			} catch {
				// non-critical
			}
		};

		init();
		const interval = setInterval(poll, POLL_INTERVAL);
		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [role, idClub]);
}
